/**
 * Mobile Home Page / Landing Page
 *
 * Mobile-optimized landing page with touch-friendly interactions,
 * optimized for SEO and conversion on mobile devices.
 */

import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useTranslation } from 'react-i18next';
import { useSEO, useHreflang, useStructuredData, SITE_CONFIG } from '../../lib/seo';

export default function MobileHomePage() {
  const { isAuthenticated } = useAuthStore();
  const { t, i18n } = useTranslation('landing');

  // SEO: Update meta tags
  useSEO({
    title: t('meta.title'),
    description: t('meta.description'),
    keywords: t('meta.keywords', 'form builder, survey maker, quiz creator, live quiz, polls, free forms, mobile forms'),
    locale: i18n.language,
  });

  // SEO: Add hreflang tags
  useHreflang('/');

  // SEO: MobileApplication schema
  const mobileAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    'name': 'xtFORM',
    'applicationCategory': 'BusinessApplication',
    'operatingSystem': 'iOS, Android, Web',
    'description': t('meta.description'),
    'url': SITE_CONFIG.domain,
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'USD',
    },
  };

  useStructuredData(mobileAppSchema, 'mobile-application');

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile Header */}
      <header className="sticky top-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100 safe-area-inset-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-lg font-bold text-gray-900">xtFORM</span>
          </div>
          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <Link
                to="/m/forms"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm active:bg-blue-700"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="px-3 py-2 text-gray-600 text-sm font-medium">
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm active:bg-blue-700"
                >
                  Start Free
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white px-4 pt-8 pb-12">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 rounded-full text-purple-700 text-xs font-medium mb-4">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple-500"></span>
            </span>
            {t('hero.badge')}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3 leading-tight">
            {t('hero.title')}
            <span className="text-blue-600"> {t('hero.titleHighlight')}</span>
          </h1>
          <p className="text-base text-gray-600 mb-6 max-w-sm mx-auto">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col gap-3 max-w-xs mx-auto">
            <Link
              to="/register"
              className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-base active:bg-blue-700 shadow-lg shadow-blue-600/25"
            >
              {t('hero.cta')}
            </Link>
            <Link
              to="/play"
              className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-base active:from-purple-700 active:to-pink-700"
            >
              {t('hero.ctaSecondary')}
            </Link>
          </div>
          <p className="mt-3 text-xs text-gray-500">{t('hero.noCreditCard')}</p>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-gray-50 py-6 px-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-blue-600">50+</div>
            <div className="text-xs text-gray-600 mt-1">{t('stats.fieldTypes')}</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-green-600">Free</div>
            <div className="text-xs text-gray-600 mt-1">Forever</div>
          </div>
        </div>
      </section>

      {/* Features Cards - Horizontal Scroll */}
      <section className="py-8 px-4">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('features.title')}</h2>
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide">
          {/* Feature 1 */}
          <div className="flex-shrink-0 w-64 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 snap-start">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{t('features.dragDrop.title')}</h3>
            <p className="text-gray-600 text-sm">{t('features.dragDrop.description')}</p>
          </div>

          {/* Feature 2 */}
          <div className="flex-shrink-0 w-64 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 snap-start">
            <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{t('features.security.title')}</h3>
            <p className="text-gray-600 text-sm">{t('features.security.description')}</p>
          </div>

          {/* Feature 3 */}
          <div className="flex-shrink-0 w-64 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 snap-start">
            <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{t('features.api.title')}</h3>
            <p className="text-gray-600 text-sm">{t('features.api.description')}</p>
          </div>

          {/* Feature 4 */}
          <div className="flex-shrink-0 w-64 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-5 snap-start">
            <div className="w-10 h-10 bg-orange-600 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{t('features.collaboration.title')}</h3>
            <p className="text-gray-600 text-sm">{t('features.collaboration.description')}</p>
          </div>
        </div>
      </section>

      {/* Live Quiz Section */}
      <section className="bg-gradient-to-br from-purple-900 to-indigo-900 py-10 px-4">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 rounded-full text-purple-300 text-xs font-medium mb-3">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {t('liveQuiz.badge')}
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('liveQuiz.title')}</h2>
          <p className="text-purple-200 text-sm mb-6">{t('liveQuiz.subtitle')}</p>
        </div>

        {/* Mini Leaderboard Preview */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20 mb-6">
          <div className="text-center mb-4">
            <p className="text-purple-300 text-xs uppercase tracking-wide">{t('liveQuiz.gamePin')}</p>
            <p className="text-3xl font-mono font-bold text-white tracking-wider">847291</p>
          </div>
          <div className="space-y-2">
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg p-3 flex items-center gap-2">
              <span className="text-lg">1</span>
              <span className="font-bold text-white flex-1 text-sm">PlayerOne</span>
              <span className="text-white font-mono text-sm">2,450</span>
            </div>
            <div className="bg-gradient-to-r from-gray-400 to-gray-500 rounded-lg p-3 flex items-center gap-2">
              <span className="text-lg">2</span>
              <span className="font-bold text-white flex-1 text-sm">QuizMaster</span>
              <span className="text-white font-mono text-sm">2,100</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Link
            to="/register"
            className="w-full py-3.5 bg-white text-purple-600 rounded-xl font-semibold text-center active:bg-purple-50"
          >
            {t('liveQuiz.cta')}
          </Link>
          <Link
            to="/play"
            className="w-full py-3.5 bg-purple-500/20 text-white border border-purple-400/30 rounded-xl font-semibold text-center active:bg-purple-500/30"
          >
            {t('liveQuiz.ctaSecondary')}
          </Link>
        </div>
      </section>

      {/* E-Commerce Section */}
      <section className="bg-gradient-to-br from-teal-900 to-emerald-900 py-10 px-4">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-500/20 rounded-full text-teal-300 text-xs font-medium mb-3">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            E-Commerce Ready
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Sell Products Online</h2>
          <p className="text-teal-200 text-sm mb-6">Create product pages, manage orders, and build your online store.</p>
        </div>

        {/* E-commerce Features */}
        <div className="space-y-3 mb-6">
          <div className="bg-white/10 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-500/30 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-teal-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm">Product Pages</h4>
              <p className="text-xs text-teal-200">With custom domains</p>
            </div>
          </div>
          <div className="bg-white/10 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-pink-500/30 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-pink-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm">Order Management</h4>
              <p className="text-xs text-teal-200">Track orders & payments</p>
            </div>
          </div>
          <div className="bg-white/10 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500/30 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm">Store Portal</h4>
              <p className="text-xs text-teal-200">Customizable storefront</p>
            </div>
          </div>
        </div>

        <Link
          to="/register"
          className="block w-full py-3.5 bg-white text-teal-600 rounded-xl font-semibold text-center active:bg-teal-50"
        >
          Start Selling Free
        </Link>
      </section>

      {/* Use Cases Quick Links */}
      <section className="py-8 px-4 bg-gray-50">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Solutions</h2>
        <div className="grid grid-cols-2 gap-3">
          <Link to="/education" className="bg-white rounded-xl p-4 shadow-sm active:bg-gray-50">
            <span className="text-2xl mb-2 block">🎓</span>
            <h3 className="font-semibold text-gray-900 text-sm">Education</h3>
            <p className="text-xs text-gray-500">For teachers</p>
          </Link>
          <Link to="/business" className="bg-white rounded-xl p-4 shadow-sm active:bg-gray-50">
            <span className="text-2xl mb-2 block">💼</span>
            <h3 className="font-semibold text-gray-900 text-sm">Business</h3>
            <p className="text-xs text-gray-500">For teams</p>
          </Link>
          <Link to="/events" className="bg-white rounded-xl p-4 shadow-sm active:bg-gray-50">
            <span className="text-2xl mb-2 block">🎉</span>
            <h3 className="font-semibold text-gray-900 text-sm">Events</h3>
            <p className="text-xs text-gray-500">For organizers</p>
          </Link>
          <Link to="/ecommerce" className="bg-white rounded-xl p-4 shadow-sm active:bg-gray-50">
            <span className="text-2xl mb-2 block">🛍️</span>
            <h3 className="font-semibold text-gray-900 text-sm">E-Commerce</h3>
            <p className="text-xs text-gray-500">Sell online</p>
          </Link>
        </div>
      </section>

      {/* Pricing - Simple */}
      <section className="py-10 px-4">
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-white text-xs font-medium mb-3">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd" />
            </svg>
            {t('pricing.badge')}
          </div>
          <div className="text-4xl font-bold mb-1">{t('pricing.price')}</div>
          <div className="text-green-100 text-sm mb-4">{t('pricing.period')}</div>
          <ul className="text-left text-sm space-y-2 mb-6">
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('pricing.features.unlimitedForms')}
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('pricing.features.unlimitedResponses')}
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('pricing.features.liveQuizzes')}
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('pricing.features.apiAccess')}
            </li>
          </ul>
          <Link
            to="/register"
            className="block w-full py-3.5 bg-white text-green-600 rounded-xl font-semibold text-center active:bg-green-50"
          >
            {t('pricing.cta')}
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 py-10 px-4 text-center">
        <h2 className="text-2xl font-bold text-white mb-3">{t('cta.title')}</h2>
        <p className="text-blue-100 text-sm mb-6">{t('cta.subtitle')}</p>
        <Link
          to="/register"
          className="inline-block w-full max-w-xs py-3.5 bg-white text-blue-600 rounded-xl font-semibold active:bg-blue-50"
        >
          {t('cta.primary')}
        </Link>
      </section>

      {/* Mobile Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4 safe-area-inset-bottom">
        <div className="flex items-center gap-2 mb-6">
          <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-lg font-bold text-white">xtFORM</span>
        </div>

        <div className="grid grid-cols-2 gap-6 text-sm mb-6">
          <div>
            <h4 className="text-white font-semibold mb-3">Product</h4>
            <ul className="space-y-2">
              <li><Link to="/register" className="hover:text-white">Get Started</Link></li>
              <li><Link to="/play" className="hover:text-white">Join Quiz</Link></li>
              <li><Link to="/docs/api" className="hover:text-white">API Docs</Link></li>
              <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
              <li><Link to="/cookies" className="hover:text-white">Cookies</Link></li>
              <li><Link to="/about" className="hover:text-white">About</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-6 text-xs text-center">
          <p>&copy; {new Date().getFullYear()} xtFORM. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
